import {useEffect, useState} from "react"
import type {ModalConfig} from "./modalProvider"
import {Button} from "../button";
import {cn} from "../../lib/fns";
import "./modal.css";

export interface ModalProps extends ModalConfig {
  onClose?: () => void,
  custom?: boolean
}

export function Modal({custom = true, width, className, ...config}: ModalProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  useEffect(() => {
    setIsVisible(true)
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [])
  
  const handleClose = () => {
    setIsVisible(false)
    setIsLoading(false)
    setTimeout(() => {
      if (config.onCancel) config.onCancel()
      if (config.onClose) config.onClose()
    }, 200)
  }
  
  const handleConfirm = async () => {
    if (config.onConfirm) {
      setIsLoading(true)
      try {
        await config.onConfirm()
        setIsVisible(false)
      } catch (error) {
        console.error("Modal confirm error:", error)
      } finally {
        setIsLoading(false)
      }
    }
    else {
      setIsVisible(false)
    }
    if (config.onClose) config.onClose()
  }
  
  const modalContent = (<div
    data-state={isVisible ? "open" : "closed"}
    style={{width}}
    className={cn("modal-content", className, config.classNames?.content)}
    onClick={(e) => e.stopPropagation()}
  >
    <div className={cn("modal-body", config.classNames?.body)}>
      <div className={"modal-title"}>{config.title}</div>
      <div className={"modal-children"}>{config.children}</div>
    </div>
    {(config.footer || config.showCancel || config.confirmText) &&
        <div className={cn("modal-footer", config.classNames?.footer)}>
          {config.footer ? (config.footer) : (<>
            {config.showCancel && (<Button
              size={"sm"}
              variant={"text"}
              className={"cancel"}
              onClick={handleClose}
              disabled={isLoading}
            >
              {config.cancelText}
            </Button>)}
            <div className={"divider"} />
            {!!config.confirmText && <Button
                size={"sm"}
                variant={"text"}
                className={"ok"}
                onClick={handleConfirm}
                loading={isLoading}
            >
              {config.confirmText}
            </Button>}
          </>)}
        </div>}
  </div>)
  
  return (<div
    data-state={isVisible ? "open" : "closed"}
    className={"modal"}
    onClick={!!config.maskClosable ? handleClose : undefined}
  >
    <div className="modal-backdrop" />
    
    {config.modalRender ? config.modalRender(modalContent) : modalContent}
  </div>)
}
